import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Container,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Web as WebIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities } from '../store/slices/entitySlice';
import { showError, showSuccess } from '../store/slices/toastSlice';
import { chatApi } from '../services/chatApi';
import { SnippetRequest, SnippetResponse } from '../types';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`snippet-tabpanel-${index}`}
      aria-labelledby={`snippet-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SnippetGenerator: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { entities, isLoading: entitiesLoading } = useAppSelector((state) => state.entities);

  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [snippet, setSnippet] = useState<SnippetResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [customizations, setCustomizations] = useState({
    theme: 'modern',
    position: 'bottom-right',
    primaryColor: '#1976d2',
    showBranding: true,
    autoOpen: false,
    welcomeMessage: 'Hello! How can I help you today?',
  });

  useEffect(() => {
    dispatch(fetchEntities());
  }, [dispatch]);

  const handleGenerateSnippet = async () => {
    if (!selectedEntityId) {
      setError('Please select an entity first');
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      const request: SnippetRequest = {
        entity_id: selectedEntityId,
      };

      const response = await chatApi.generateSnippet(request);
      setSnippet(response);

      // Persist snippet data for demo page
      localStorage.setItem('asknow-demo-snippet', JSON.stringify(response));
      localStorage.setItem('asknow-demo-config', JSON.stringify(customizations));

      dispatch(showSuccess({
        title: 'Snippet Generated',
        message: 'JavaScript snippet generated successfully',
        duration: 3000,
      }));
    } catch (error: any) {
      setError(error.message);
      dispatch(showError({
        title: 'Generation Failed',
        message: error.message,
        duration: 5000,
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(showSuccess({
        title: 'Copied!',
        message: `${type} copied to clipboard`,
        duration: 2000,
      }));
    } catch (error) {
      dispatch(showError({
        title: 'Copy Failed',
        message: 'Failed to copy to clipboard',
        duration: 3000,
      }));
    }
  };

  const downloadSnippet = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectedEntity = entities.find(e => e.entity_id === selectedEntityId);

  const generateCustomSnippet = () => {
    if (!snippet) return '';

    return `
<!-- AskNow Chatbot Widget - Customized -->
<div id="asknow-chatbot-${snippet.entity_id}"></div>
<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://cdn.asknow.com/widget.js';
    script.onload = function() {
      AskNowWidget.init({
        entityId: '${snippet.entity_id}',
        apiKey: '${snippet.api_key}',
        theme: '${customizations.theme}',
        position: '${customizations.position}',
        primaryColor: '${customizations.primaryColor}',
        showBranding: ${customizations.showBranding},
        autoOpen: ${customizations.autoOpen},
        welcomeMessage: '${customizations.welcomeMessage}',
        // Additional customization options
        borderRadius: '8px',
        fontSize: '14px',
        zIndex: 9999
      });
    };
    document.head.appendChild(script);
  })();
</script>
`.trim();
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            JavaScript Snippet Generator
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate embeddable chatbot widgets for your entities
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Configuration Panel */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuration
                </Typography>

                {/* Entity Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Entity</InputLabel>
                  <Select
                    value={selectedEntityId}
                    onChange={(e) => setSelectedEntityId(e.target.value)}
                    label="Select Entity"
                    disabled={entitiesLoading}
                  >
                    {entities.map((entity) => (
                      <MenuItem key={entity.entity_id} value={entity.entity_id}>
                        {entity.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedEntity && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Entity Details:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {selectedEntity.name}
                    </Typography>
                    {selectedEntity.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Description:</strong> {selectedEntity.description}
                      </Typography>
                    )}
                    {selectedEntity.processed_data && (
                      <Chip
                        label="AI Ready"
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}

                {/* Customization Options */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Widget Customization:
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={customizations.theme}
                    onChange={(e) => setCustomizations(prev => ({ ...prev, theme: e.target.value }))}
                    label="Theme"
                  >
                    <MenuItem value="modern">Modern</MenuItem>
                    <MenuItem value="classic">Classic</MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={customizations.position}
                    onChange={(e) => setCustomizations(prev => ({ ...prev, position: e.target.value }))}
                    label="Position"
                  >
                    <MenuItem value="bottom-right">Bottom Right</MenuItem>
                    <MenuItem value="bottom-left">Bottom Left</MenuItem>
                    <MenuItem value="top-right">Top Right</MenuItem>
                    <MenuItem value="top-left">Top Left</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Primary Color"
                  type="color"
                  value={customizations.primaryColor}
                  onChange={(e) => setCustomizations(prev => ({ ...prev, primaryColor: e.target.value }))}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Welcome Message"
                  value={customizations.welcomeMessage}
                  onChange={(e) => setCustomizations(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={customizations.showBranding}
                      onChange={(e) => setCustomizations(prev => ({ ...prev, showBranding: e.target.checked }))}
                    />
                  }
                  label="Show AskNow Branding"
                  sx={{ mb: 1 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={customizations.autoOpen}
                      onChange={(e) => setCustomizations(prev => ({ ...prev, autoOpen: e.target.checked }))}
                    />
                  }
                  label="Auto Open Widget"
                />

                {/* Generate Button */}
                <Button
                  variant="contained"
                  onClick={handleGenerateSnippet}
                  disabled={!selectedEntityId || isGenerating}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <CodeIcon />}
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  {isGenerating ? 'Generating...' : 'Generate Snippet'}
                </Button>

                {/* Demo Button */}
                {snippet && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/sample-website', { state: { snippet, customizations } })}
                    startIcon={<WebIcon />}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    View Live Demo
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Code Display Panel */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="HTML Snippet" />
                    <Tab label="JavaScript Only" />
                    <Tab label="Customized" />
                  </Tabs>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {!snippet && !isGenerating && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '300px',
                      flexDirection: 'column',
                      gap: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <CodeIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                    <Typography variant="h6" color="text.secondary">
                      Select an entity and generate a snippet
                    </Typography>
                  </Box>
                )}

                {snippet && (
                  <>
                    {/* HTML Snippet Tab */}
                    <TabPanel value={tabValue} index={0}>
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">HTML Snippet</Typography>
                          <Box>
                            <Tooltip title="Copy HTML">
                              <IconButton onClick={() => copyToClipboard(snippet.snippet_html, 'HTML snippet')}>
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download HTML">
                              <IconButton onClick={() => downloadSnippet(snippet.snippet_html, 'asknow-widget.html')}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Paper elevation={0} sx={{ bgcolor: 'grey.900', color: 'white', p: 2, borderRadius: 1 }}>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                            {snippet.snippet_html}
                          </pre>
                        </Paper>
                      </Box>
                    </TabPanel>

                    {/* JavaScript Only Tab */}
                    <TabPanel value={tabValue} index={1}>
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">JavaScript Only</Typography>
                          <Box>
                            <Tooltip title="Copy JavaScript">
                              <IconButton onClick={() => copyToClipboard(snippet.snippet_js, 'JavaScript code')}>
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download JavaScript">
                              <IconButton onClick={() => downloadSnippet(snippet.snippet_js, 'asknow-widget.js')}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Paper elevation={0} sx={{ bgcolor: 'grey.900', color: 'white', p: 2, borderRadius: 1 }}>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                            {snippet.snippet_js}
                          </pre>
                        </Paper>
                      </Box>
                    </TabPanel>

                    {/* Customized Tab */}
                    <TabPanel value={tabValue} index={2}>
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Customized Snippet</Typography>
                          <Box>
                            <Tooltip title="Copy Customized">
                              <IconButton onClick={() => copyToClipboard(generateCustomSnippet(), 'Customized snippet')}>
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Customized">
                              <IconButton onClick={() => downloadSnippet(generateCustomSnippet(), 'asknow-widget-custom.html')}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Paper elevation={0} sx={{ bgcolor: 'grey.900', color: 'white', p: 2, borderRadius: 1 }}>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                            {generateCustomSnippet()}
                          </pre>
                        </Paper>
                      </Box>
                    </TabPanel>

                    {/* API Key Info */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        API Key Information:
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          API Key:
                        </Typography>
                        <Box
                          sx={{
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            padding: '8px',
                            borderRadius: '4px',
                            mt: 1,
                            fontSize: '0.9rem'
                          }}
                        >
                          {snippet.api_key}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Generated: {new Date(snippet.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SnippetGenerator;
